import React from 'react';
import { TableCell, TableRow, Box, Typography, IconButton, alpha } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { ColumnConfig } from '../../utils/customerSentimentutils';
import MuiTablePrimaryHeaderResizeArea from '../table/MuiTablePrimaryHeaderResizeArea';

interface TableHeaderProps {
  columns: ColumnConfig[];
  onSort: (columnId: string) => void;
  onFilterClick: (columnId: string, event: React.MouseEvent<HTMLElement>) => void;
  isFilterActive: (columnId: string) => boolean;
  renderFilterMenu: (columnId: string, columnLabel: string) => React.ReactNode;
  getColumnWidth?: (columnId: string) => string | undefined;
  getColumnMinWidth?: (columnId: string) => string | undefined;
  /** Pixel width from column resize hook (overrides percentage width when set). */
  getColumnWidthPx?: (columnId: string) => number;
  onBeginColumnResize?: (columnId: string, e: React.MouseEvent | React.TouchEvent) => void;
  resizingColumnId?: string | null;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  onSort,
  onFilterClick,
  isFilterActive,
  renderFilterMenu,
  getColumnWidth,
  getColumnMinWidth,
  getColumnWidthPx,
  onBeginColumnResize,
  resizingColumnId = null,
}) => {
  return (
    <TableRow sx={{ backgroundColor: SEARCE_COLORS.primary }}>
      {columns.map((column) => {
        // Special handling for two-line headers (Feedback Responses table)
        const isTwoLineHeader = ['deliveryCompletion', 'deliveryTimeline', 'deliveryClosure', 'teamTechnical', 'teamSupport', 'teamResponsiveness'].includes(column.id);
        const categoryLabel = column.id.startsWith('delivery') ? 'Delivery' : column.id.startsWith('team') ? 'Team' : null;
        const metricLabel = column.id === 'deliveryCompletion' ? 'Completion' :
                          column.id === 'deliveryTimeline' ? 'Timeline' :
                          column.id === 'deliveryClosure' ? 'Closure & Handover' :
                          column.id === 'teamTechnical' ? 'Technical Expertise' :
                          column.id === 'teamSupport' ? 'Support Provided' :
                          column.id === 'teamResponsiveness' ? 'Responsiveness' : null;

        const widthPx = getColumnWidthPx?.(column.id);
        return (
          <TableCell
            key={column.id}
            align="center"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              color: SEARCE_COLORS.white,
              textTransform: 'uppercase',
              py: 1.5,
              borderBottom: 'none',
              ...(widthPx != null
                ? {
                    width: widthPx,
                    minWidth: widthPx,
                    maxWidth: widthPx,
                    boxSizing: 'border-box',
                    position: 'relative',
                    paddingRight: '18px',
                  }
                : {
                    width: getColumnWidth?.(column.id),
                    minWidth: getColumnMinWidth?.(column.id),
                  }),
              overflow: 'visible',
              textOverflow: 'clip',
              whiteSpace: column.id === 'date' ? 'nowrap' : 'normal',
            }}
          >
            {isTwoLineHeader ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Box sx={{ fontSize: '0.65rem', opacity: 0.85, fontWeight: 500, lineHeight: 1.2 }}>
                  {categoryLabel}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, flexWrap: 'nowrap' }}>
                  <Typography
                    component="span"
                    onClick={() => onSort(column.id)}
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      color: SEARCE_COLORS.white,
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      userSelect: 'none',
                      textAlign: 'center',
                      minWidth: 0,
                      '&:hover': { opacity: 0.85 },
                    }}
                  >
                    {metricLabel}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFilterClick(column.id, e);
                    }}
                    sx={{
                      flexShrink: 0,
                      color: isFilterActive(column.id)
                        ? SEARCE_COLORS.warning
                        : SEARCE_COLORS.white,
                      '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.15) },
                      padding: '2px',
                    }}
                  >
                    <FilterListIcon fontSize="small" sx={{ fontSize: '0.7rem' }} />
                  </IconButton>
                  {renderFilterMenu(column.id, column.label)}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'nowrap', minWidth: 0 }}>
                <Typography
                  component="span"
                  onClick={() => onSort(column.id)}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: SEARCE_COLORS.white,
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    userSelect: 'none',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  {column.label}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFilterClick(column.id, e);
                  }}
                  sx={{
                    flexShrink: 0,
                    color: isFilterActive(column.id) 
                      ? SEARCE_COLORS.warning 
                      : SEARCE_COLORS.white,
                    '&:hover': { backgroundColor: alpha(SEARCE_COLORS.white, 0.15) },
                    padding: '2px',
                    ml: 0.5,
                  }}
                >
                  <FilterListIcon fontSize="small" sx={{ fontSize: '0.7rem' }} />
                </IconButton>
                {renderFilterMenu(column.id, column.label)}
              </Box>
            )}
            {onBeginColumnResize ? (
              <MuiTablePrimaryHeaderResizeArea
                columnId={column.id}
                onBeginResize={onBeginColumnResize}
                isResizing={resizingColumnId === column.id}
              />
            ) : null}
          </TableCell>
        );
      })}
    </TableRow>
  );
};
